import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { MoreVertical, Pencil, Archive, ArchiveRestore } from 'lucide-react'
import type { SharedProps } from '@/types'
import type CrmFundraisingDonationNature from '@/types/serializers/CrmFundraisingDonationNature'
import { EditDonationNatureSheet } from './EditDonationNatureSheet'

export function DonationNatureActions({
  nature,
}: {
  nature: CrmFundraisingDonationNature
}) {
  const { auth } = usePage<{ auth: SharedProps['auth'] }>().props
  const [editOpen, setEditOpen] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)

  if (auth.user?.role !== 'space_admin') return null

  function handleArchive() {
    router.patch(`/fundraising/donation_natures/${nature.id}/archive`, {}, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        setConfirmArchive(false)
        toast.success('Nature de don archivee avec succes')
      },
    })
  }

  function handleRestore() {
    router.patch(`/fundraising/donation_natures/${nature.id}/restore`, {}, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => toast.success('Nature de don restauree avec succes'),
    })
  }

  return (
    <div className="text-right" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-7 shrink-0">
            <MoreVertical className="size-4" />
            <span className="sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {nature.discarded ? (
            <DropdownMenuItem onClick={handleRestore} className="flex items-center gap-2">
              <ArchiveRestore className="size-4" />
              Restaurer
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem onClick={() => setEditOpen(true)} className="flex items-center gap-2">
                <Pencil className="size-4" />
                Renommer
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setConfirmArchive(true)}
                className="flex items-center gap-2 text-destructive focus:text-destructive"
              >
                <Archive className="size-4" />
                Archiver
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <EditDonationNatureSheet
        nature={editOpen ? nature : null}
        onOpenChange={setEditOpen}
      />

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver cette nature de don ?</AlertDialogTitle>
            <AlertDialogDescription>
              Elle disparaitra des listes et des formulaires. Vous pourrez la
              restaurer plus tard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archiver</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
